import { ArrayUtil, LayoutNameResolver, LogManager, StringUtil, ValueOrFunction, ValueResolver } from "@mcleod/core";
import { Layout } from "../components/layout/Layout";
import { LayoutProps } from "../components/layout/LayoutProps";
import { Table } from "../components/table/Table";
import { DataSource, DataSourceAction } from "../databinding/DataSource";
import { DataSourceExecutionEvent } from "../events/DataSourceExecutionEvent";
import { LayoutTabLauncher } from "./LayoutTabLauncher";

const log = LogManager.getLogger("components.page.ListLayoutLauncher");

export interface ListLayoutLauncherProps {
    listLayout: ValueOrFunction<string>;
    keyValues: ValueOrFunction<string[]>;
}

export class ListLayoutLauncher {
    private readonly listLayout: string;
    private readonly keyValues: string[];
    private readonly isMultiValueSearch: boolean;
    private layout: Layout;

    private constructor(props: ListLayoutLauncherProps) {
        this.listLayout = LayoutNameResolver.resolve(props.listLayout);
        this.keyValues = ValueResolver.resolve(props.keyValues)?.filter(value => !StringUtil.isEmptyString(value)) ?? [];
        this.isMultiValueSearch = this.keyValues.length > 1;
    }

    /**
     * Opens a list layout in a new tab, searches for the specified value,
     * and automatically selects the matching table row.
     *
     * @param props - The props to pass to the list layout launcher
     */
    public static async open(props: ListLayoutLauncherProps) {
        await new ListLayoutLauncher(props).open();
    }

    private getLayoutProps(): Partial<LayoutProps> {
        if (this.keyValues?.length > 1) {
            return { preventAutoSearchOnLoad: true };
        }
        return null;
    }

    private async open(): Promise<Layout> {
        if (this.listLayout == null)
            return null;

        try {
            this.layout = await LayoutTabLauncher.open(this.listLayout, this.getLayoutProps());
            if (!this.isMultiValueSearch) {
                this.setLayoutBusy();
            }
            this.searchLayoutIfNeeded();
            return this.layout;
        } catch (error) {
            log.error("Failed to navigate to list layout:", error);
            throw error;
        }
    }

    private setLayoutBusy() {
        this.layout.busy = true;
        // Safety timeout: ensure busy state is cleared
        setTimeout(() => {
            if (this.layout?.busy === true) {
                log.debug("Clearing busy state after timeout for layout: %s", this.layout.layoutName);
                this.layout.busy = false;
            }
        }, 5000);
    }

    private searchLayoutIfNeeded() {
        const ctx = this.getSearchContext();
        if (ctx == null) {
            this.layout.busy = false;
            return;
        }
        const { table, dataSource, keyField } = ctx;

        const searchWasExecuted = dataSource.rowCount > 0 && dataSource.lastSearch != null;
        let keyValueText: string;

        if (this.isMultiValueSearch) {
            keyValueText = `in ${this.keyValues.join(",")}`;
        } else {
            keyValueText = this.keyValues[0];
            this.handleRowSelection(table, keyValueText, searchWasExecuted);
        }

        if (searchWasExecuted !== true && dataSource.actionInProgress !== DataSourceAction.SEARCH) {
            dataSource.search({ [keyField]: keyValueText });
        }
    }

    private getSearchContext(): { table: Table; dataSource: DataSource; keyField: string } | null {
        if (ArrayUtil.isEmptyArray(this.keyValues)) {
            log.debug("No search filter provided for layout: %s", this.layout.layoutName);
            return null;
        }
        const table = this.layout.getRecursiveChildren().find(c => c.serializationName === "table") as Table;
        if (table == null) {
            log.debug("Unable to find table in layout: %s", this.layout.layoutName);
            return null;
        }
        const dataSource = table.dataSource;
        if (dataSource == null) {
            log.debug("table: %s in layout: %s has no dataSource", table.id, this.layout.layoutName);
            return null;
        }
        const keyField = dataSource.getMetadataFromCache()?.keyFields?.[0];
        if (keyField == null) {
            log.debug("dataSource: %s in layout: %s has no key field", dataSource.id, this.layout.layoutName);
            return null;
        }

        return { table, dataSource, keyField };
    }

    private handleRowSelection(table: Table, keyValue: string, searchWasExecuted: boolean): void {
        if (searchWasExecuted) {
            this.doubleClickRow(table, keyValue);
            return;
        }
        const executionListener = (event: DataSourceExecutionEvent) => {
            if (event.getAction() === DataSourceAction.SEARCH) {
                this.doubleClickRow(table, keyValue);
            }
        };
        executionListener.runsOnce = true;
        table.dataSource.addAfterExecutionListener(executionListener);
    }

    private doubleClickRow(table: Table, keyValue: string): void {
        const tableRow = table.getRowByDataKeyValue(keyValue);
        if (tableRow == null) {
            this.layout.busy = false;
            return;
        }

        tableRow.populateDOMIfNeeded().then(() => {
            tableRow.doubleClicked(new window.MouseEvent('dblclick', {
                cancelable: true,
                bubbles: true,
                detail: 2,
            }));
        }).finally(() => {
            // small delay to allow the overlay to slide in before setting the busy prop to false
            setTimeout(() => {
                this.layout.busy = false;
                tableRow.scrollIntoView();
            }, 200);
        });
    }
}
